/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/**
 * `RedactionPlaceholder` — a correctly-sized, inline stand-in rendered while the
 * redaction extension handler / `ScribbleBar` component is still resolving.
 *
 * The redaction node is rendered through the async extension pipeline
 * (`render: async () => RedactionMarker`), so there is a brief window before the
 * real `ScribbleBar` mounts. Without a sized placeholder the node collapses to
 * empty text (and can briefly wrap), then jumps to the final scribble width once
 * it mounts, producing a visible layout shift. This placeholder reserves the
 * exact inline footprint the `ScribbleBar` will occupy so the surrounding text
 * never moves; the scribble simply fades in over the reserved box.
 *
 * The box dimensions here MUST stay in sync with the `ScribbleBar` container in
 * `./index.tsx` (`height`, `aspectRatio`, `display`, `verticalAlign`,
 * `borderRadius`). Any change to the bar's size must be mirrored here.
 *
 * The box is filled as an ADS skeleton (shimmering between `color.skeleton` and
 * `color.skeleton.subtle`) so the reserved space reads as "loading" rather than as
 * a gap in the sentence. Users who prefer reduced motion get the static fill.
 */
import React from 'react';

// `keyframes` has no `@atlaskit/css` equivalent, so the shimmer is declared with
// Compiled directly, the same way `@atlaskit/skeleton` does. The box itself stays
// on the strict API below.
import { css, keyframes } from '@compiled/react';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	placeholder: {
		// Keep these values identical to `ScribbleBar`'s container box so the
		// reserved space matches the final rendered bar exactly (no layout shift).
		display: 'inline-flex',
		height: '1.125rem',
		aspectRatio: '3.4 / 1',
		verticalAlign: 'middle',
		borderRadius: token('radius.xsmall'),
		// Skeleton fill (the shimmer below alternates it with the subtle variant).
		// Redacted content must not be selectable.
		backgroundColor: token('color.skeleton'),
		userSelect: 'none',
	},
});

// Same cadence and color pair as the `@atlaskit/skeleton` shimmer.
const shimmerKeyframes = keyframes({
	from: {
		backgroundColor: token('color.skeleton'),
	},
	to: {
		backgroundColor: token('color.skeleton.subtle'),
	},
});

const shimmerStyles = css({
	animationName: shimmerKeyframes,
	animationDuration: '1.5s',
	animationDirection: 'alternate',
	animationIterationCount: 'infinite',
	animationTimingFunction: 'linear',
	'@media (prefers-reduced-motion: reduce)': {
		animationName: 'none',
	},
});

/**
 * Inline, non-interactive skeleton that reserves the `ScribbleBar` footprint
 * during load. Hidden from assistive tech since it carries no meaning on its own
 * (the mounted bar provides the accessible label).
 */
export const RedactionPlaceholder = (): React.JSX.Element => (
	<span
		css={[styles.placeholder, shimmerStyles]}
		data-testid="guard-redaction-placeholder"
		aria-hidden="true"
		// Same editor wrapper opt-out marker as `ScribbleBar`, so the editor's inline
		// extension chrome does not flash around the placeholder and then vanish when
		// the bar mounts.
		data-inline-extension-layout="flow"
	/>
);
